import type { Annotation } from "building-types/annotation/annotationTypes";
import type { Casework } from "building-types/casework/caseworkTypes";
import type { WallCasework } from "building-types/casework/wallCaseworkTypes";
import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { DocForeignKeyTable } from "building-types/doc/docForeignKeyTable";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { DocEntityBase, FormulaDisabledDocEntityBase } from "building-types/entities/docEntityBase";
import type { DocEntityHandle, RoomLayoutInstanceHandle } from "building-types/handles/docEntityHandleTypes";
import type { Opening, OpeningNoExpr } from "building-types/openings/opening";
import type { PropInstance } from "building-types/props/propInstanceTypes";
import type { Prop } from "building-types/props/propTypes";
import type { ContentTemplateInstance } from "building-types/views/planSets";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { DocEntityType } from "../doc/docEntityTypes";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { EntityInstanceType } from "./roomLayoutEligibleDocEntity";

export interface RoomLayout extends DocEntityBase<"ROOM_LAYOUT"> {
  readonly id: PrimaryEntityId<"ROOM_LAYOUT">;
  readonly parentId: DocEntityParentId<"ROOM">; // room id (a room layout is tightly coupled to a source room)
}

export interface RoomLayoutInstance extends FormulaDisabledDocEntityBase<"ROOM_LAYOUT_INSTANCE"> {
  readonly id: PrimaryEntityId<"ROOM_LAYOUT_INSTANCE">;
  readonly parentId: DocEntityParentId<"ROOM">; // room id where the room layout instance is applied
  readonly roomLayoutId: ForeignEntityId<"ROOM_LAYOUT">;
  readonly edgeIdRemapping: { [instanceEdgeId: string]: string | null }; // instance edgeId -> source layout edgeId
  readonly suppressedEntities: DocForeignKeyTable<RoomLayoutEntityType, boolean>;
}

export type RoomLayoutDocEntity =
  | Opening
  | OpeningNoExpr
  | PropInstance
  | WallCasework
  | Annotation
  | ContentTemplateInstance;
export type RoomLayoutBuildingEntity = GeneratedWindowPlacement | GeneratedDoorPlacement | Prop | Casework | Annotation; // annotation needs to be fixed
export type RoomLayoutEntity = RoomLayoutDocEntity | RoomLayoutBuildingEntity;

/**
 * Single source of truth for the set of {@link DocEntityType}s that can be room layout entities.
 * This way the type and runtime membership checks cannot drift out of sync.
 */
export const roomLayoutEntityTypes = [
  "ANNOTATION",
  "CASEWORK",
  "OPENING",
  "PROP_INSTANCE",
  "CONTENT_TEMPLATE_INSTANCE",
] satisfies readonly DocEntityType[];

export type RoomLayoutEntityType = (typeof roomLayoutEntityTypes)[number];

export const handleAuxiliaryField: keyof Pick<RoomLayoutBuildingEntity, "handleAuxiliary"> = "handleAuxiliary";

export type RoomLayoutEntityHandle = Extract<DocEntityHandle, { type: RoomLayoutEntityType }>;

export interface RoomLayoutEligibleEntity {
  readonly entityType: RoomLayoutEntityType;
  readonly id: ForeignEntityId<RoomLayoutEntityType>;
  readonly edgeId?: string;
  readonly edgeIds?: string[];
  readonly parentId?: DocEntityParentId<"ROOM" | "ROOM_LAYOUT" | "EDGE">;
}

/**
 * A proxy for @type {RoomLayoutDocEntity} due to needing to support both
 * room layout entities with either a single edgeId or multiple edgeIds
 * (currently only @type {WallCaseworkDynamicDeprecated} implements multiple edgeIds).
 */
export interface EntityInstance {
  readonly entityInstanceType?: EntityInstanceType;
  readonly parentId?: DocEntityParentId<"ROOM" | "ROOM_LAYOUT" | "EDGE">;
  readonly edgeId?: string | null;
  readonly edgeIds?: string[] | null;
  readonly handleOverride?: RoomLayoutInstanceHandle;
  readonly mirrored?: boolean;
  readonly instanceId?: string;
}
